import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import UserDocumentApi, {
  ACCEPTED_DOCUMENT_TYPES,
  MAX_BATCH_FILES,
  UserDocumentDto,
} from '../../api/UserDocumentApi';
import CourtEventApi, { CourtEventDto } from '../../api/CourtEventApi';
import { fetchTicketsPage, TICKETS_PAGE_SIZE } from '../../api/fetchTicketsPage';

// Enhanced interface to include ticket information
interface CourtEventWithTicket {
  CourtEvent_guid: string;
  CourtEvent_Start: number;
  CourtEvent_End: number;
  CourtEvent_Title: string;
  CourtEvent_Desc: string;
  Ticket_guid: string;
  ticketNumber: string;
  ticketGuid: string;
}
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  DialogContentText,
  Link as MuiLink,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import TicketIcon from '@mui/icons-material/Receipt';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LockResetIcon from '@mui/icons-material/LockReset';
import NoteIcon from '@mui/icons-material/Note';
import MessageIcon from '@mui/icons-material/Message';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EventIcon from '@mui/icons-material/Event';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ManagerTicketsList from '../../components/ManagerTicketsList/ManagerTicketsList';
import AgreementVersionPanel from '../../components/AgreementVersionPanel/AgreementVersionPanel';
import { formatDateOnly } from '../../utils/formatDateOnly';
import CaseStatusTimeline, { stageForStatus, CLOSED_STAGE_INDEX } from '../../components/CaseStatusTimeline/CaseStatusTimeline';
import { deriveStageIndex } from '../../utils/caseStage';
import { ContextTicketDto, TicketStatusNameType } from '../HomeView/HomeView';
import ManagerApi from '../../api/ManagerApi';
import './ManagerUserView.scss';
import { getStatusColor } from '../../utils/GetStatusColor';
import { clientDisplayName } from '../../utils/clientName';

const SOURCE_OPTIONS = [
  'OTR',
  'CDL Consultant',
  'CDL Legal',
  'TVC',
  'LRIS',
  'AVVO',
  'Simplex',
  'ARAG',
  'US Legal',
  'Texas Legal',
  'Legal Shield',
  'Repeat',
  'Google',
  'Other',
];

interface UserNoteDto {
  UserNote_guid: string;
  UserNote_Title: string;
  UserNote_Desc: string;
  Ticket_guid: string | null;
  // Who wrote it and when. The API has always returned both (UserNoteController.asDto); this screen
  // just never showed them, so a note here had no author and no date while the case-page side panel
  // showed both. Null author on rows written before author capture existed.
  UserNote_AuthorName: string | null;
  UserNote_CreatedAt: string | null;
}

interface UserDto {
  User_guid: string;
  User_FirstName: string;
  User_MiddleName: string;
  User_LastName: string;
  User_Email: string | null; // Null when no address is on file
  User_DOB: string;
  User_Active: boolean;
  User_EmailVerified: string | null;
  User_TermsOfService: string;
  User_PhoneNumber: string | null;
  User_DriversLicenseNumber: string | null;
  User_LicenseType: string | null;
  User_Source: string | null;
}

interface UserFormData {
  User_FirstName: string;
  User_MiddleName: string;
  User_LastName: string;
  User_Email: string;
  User_Active: boolean;
  User_DOB: string;
  User_PhoneNumber: string;
  User_DriversLicenseNumber: string;
  User_LicenseType: string;
  User_Source: string;
}

const ManagerUserView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userGuid = searchParams.get('User_guid');

  const [user, setUser] = useState<UserDto | null>(null);
  const [tickets, setTickets] = useState<ContextTicketDto[]>([]);
  const [ticketsLoadingMore, setTicketsLoadingMore] = useState(false);
  const [ticketsCurrentPage, setTicketsCurrentPage] = useState(0);
  const [ticketsHasMore, setTicketsHasMore] = useState(false);
  const [userNotes, setUserNotes] = useState<UserNoteDto[]>([]);
  const [userDocuments, setUserDocuments] = useState<UserDocumentDto[]>([]);
  const [formData, setFormData] = useState<UserFormData>({
    User_FirstName: '',
    User_MiddleName: '',
    User_LastName: '',
    User_Email: '',
    User_Active: true,
    User_DOB: '',
    User_PhoneNumber: '',
    User_DriversLicenseNumber: '',
    User_LicenseType: '',
    User_Source: '',
  });
  const [loading, setLoading] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  // Files the server refused during a batch upload, named so staff can re-send just those.
  const [documentUploadFailures, setDocumentUploadFailures] = useState<
    Array<{ name: string; error: string }>
  >([]);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusUpdatingGuid, setStatusUpdatingGuid] = useState<string | null>(null);
  const [pendingStageChange, setPendingStageChange] = useState<{
    ticketGuid: string;
    statusName: string;
    stageLabel: string;
    matterLabel: string;
  } | null>(null);
  const [caseFilter, setCaseFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Document menu state
  const [docMenuAnchorEl, setDocMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDocument, setSelectedDocument] = useState<UserDocumentDto | null>(null);
  const [deleteDocDialogOpen, setDeleteDocDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<UserDocumentDto | null>(null);
  const [deletingDocument, setDeletingDocument] = useState(false);
  
  // Password reset state
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Delete-client state (soft delete; Lloyd Jul 8)
  const [deleteClientOpen, setDeleteClientOpen] = useState(false);
  const [deletingClient, setDeletingClient] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Court Events state
  const [courtEvents, setCourtEvents] = useState<CourtEventWithTicket[]>([]);
  const [courtEventsLoading, setCourtEventsLoading] = useState(false);
  const [courtEventError, setCourtEventError] = useState<string | null>(null);
  
  // Event Modal state
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CourtEventWithTicket | null>(null);
  /** Which of the client's cases a NEW event attaches to. Unused when editing. */
  const [eventTicketGuid, setEventTicketGuid] = useState<string>('');
  const [eventForm, setEventForm] = useState({
    CourtEvent_StartDate: '',
    CourtEvent_StartTime: '',
    CourtEvent_EndDate: '',
    CourtEvent_EndTime: '',
    CourtEvent_Title: '',
    CourtEvent_Desc: '',
  });
  
  // User Notes state
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<UserNoteDto | null>(null);
  const [noteFormData, setNoteFormData] = useState({
    title: '',
    description: ''
  });
  /*
   * Which case this note belongs to (Hannah, Aug 18; Lloyd agreed).
   *
   * The case page has always filed notes correctly. This screen posted NO Ticket_guid at all, so
   * every note written here was unfiled forever and the list showed no case attribution — on a
   * client with four cases, which is common, nobody could tell which one a note was about.
   *
   * Empty string means "about the client, not a case", which is a legitimate answer and stays
   * available: a note about a person's circumstances is not a note about a citation.
   */
  const [noteTicketGuid, setNoteTicketGuid] = useState<string>('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);

  // Fetch user details
  const fetchUser = async () => {
    if (!userGuid) return;
    
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager-user/users?User_guid=${userGuid}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch user: ${response.status} ${response.statusText}`);
      }

      const data: UserDto[] = await response.json();
      if (data.length === 0) {
        throw new Error('User not found');
      }

      const userData = data[0];
      setUser(userData);
      setFormData({
        User_FirstName: userData.User_FirstName,
        User_MiddleName: userData.User_MiddleName || '',
        User_LastName: userData.User_LastName,
        User_Email: userData.User_Email || '',
        User_Active: userData.User_Active,
        User_DOB: userData.User_DOB,
        User_PhoneNumber: userData.User_PhoneNumber || '',
        User_DriversLicenseNumber: userData.User_DriversLicenseNumber || '',
        User_LicenseType: userData.User_LicenseType || '',
        User_Source: userData.User_Source || '',
      });
      
    } catch (err) {
      console.error('Error fetching user:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user tickets
  const fetchUserTickets = async (page = 0, appendResults = false) => {
    if (!userGuid) return;

    if (!appendResults) {
      setTicketsLoading(true);
    } else {
      setTicketsLoadingMore(true);
    }

    try {
      const { tickets: data, hasMore } = await fetchTicketsPage({
        from: page * TICKETS_PAGE_SIZE,
        size: TICKETS_PAGE_SIZE,
        userGuid,
      });

      if (appendResults) {
        setTickets((prev) => [...prev, ...data]);
      } else {
        setTickets(data);
      }

      setTicketsHasMore(hasMore);
      setTicketsCurrentPage(page);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      // Don't show error for tickets, just log it
    } finally {
      setTicketsLoading(false);
      setTicketsLoadingMore(false);
    }
  };

  const handleLoadMoreTickets = () => {
    fetchUserTickets(ticketsCurrentPage + 1, true);
  };

  // Fetch user notes
  const fetchUserNotes = async () => {
    if (!userGuid) return;
    
    setNotesLoading(true);

    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/user-note/list?User_guid=${userGuid}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch notes: ${response.status} ${response.statusText}`);
      }

      const data: UserNoteDto[] = await response.json();
      setUserNotes(data);
      
    } catch (err) {
      console.error('Error fetching notes:', err);
      // Don't show error for notes, just log it
    } finally {
      setNotesLoading(false);
    }
  };

  // Fetch user documents
  const fetchUserDocuments = async () => {
    if (!userGuid) return;
    
    setDocumentsLoading(true);

    try {
      const documents = await UserDocumentApi.getUserDocuments(userGuid);
      setUserDocuments(documents);
      
    } catch (err) {
      console.error('Error fetching documents:', err);
      // Don't show error for documents, just log it
    } finally {
      setDocumentsLoading(false);
    }
  };

  // Upload one or more documents at the client level. Files are reported individually so an
  // unsupported one does not take the rest of the batch down with it.
  const handleDocumentUpload = async (files: File[]) => {
    if (!userGuid || files.length === 0) return;

    if (files.length > MAX_BATCH_FILES) {
      setError(`Select up to ${MAX_BATCH_FILES} files at a time.`);
      return;
    }

    setUploadingDocument(true);
    setError(null);
    setDocumentUploadFailures([]);

    try {
      const result = await UserDocumentApi.uploadDocuments(userGuid, files);

      setDocumentUploadFailures(result.failed);
      if (result.uploadedCount > 0) {
        setSuccess(
          result.failedCount === 0
            ? `Uploaded ${result.uploadedCount} file${result.uploadedCount === 1 ? '' : 's'}`
            : `Uploaded ${result.uploadedCount} of ${files.length} files`,
        );
      } else {
        setError('None of the selected files could be uploaded.');
      }
      fetchUserDocuments(); // Refresh the documents list
    } catch (err) {
      console.error('Error uploading documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload documents');
    } finally {
      setUploadingDocument(false);
    }
  };

  // Fetch court events for all user tickets
  const fetchCourtEvents = async () => {
    if (!userGuid) return;
    
    setCourtEventsLoading(true);
    setCourtEventError(null);

    try {
      // Get all court events for this user with a single API call
      const events = await CourtEventApi.listCourtEvents({ User_guid: userGuid });
      
      // Enrich events with ticket information from our tickets data
      const enrichedEvents: CourtEventWithTicket[] = events.map(event => {
        // Find the matching ticket for this event using Ticket_guid
        const matchingTicket = tickets.find(t => t.ticket.Ticket_guid === event.Ticket_guid);
        
        return {
          ...event,
          ticketNumber: matchingTicket?.ticket.Ticket_Number || `Unknown ticket`,
          ticketGuid: event.Ticket_guid
        };
      });
      
      // Sort events by start time
      enrichedEvents.sort((a, b) => a.CourtEvent_Start - b.CourtEvent_Start);
      setCourtEvents(enrichedEvents);
      
    } catch (err) {
      console.error('Error fetching court events:', err);
      setCourtEventError(err instanceof Error ? err.message : 'Failed to load court events');
    } finally {
      setCourtEventsLoading(false);
    }
  };

  // Delete document
  const handleDeleteDocument = async (document: UserDocumentDto) => {
    if (!userGuid) return;
    
    setDeletingDocument(true);
    setError(null);

    try {
      await UserDocumentApi.deleteDocument(userGuid, document.UserDocument_guid);
      setSuccess('Document deleted successfully');
      fetchUserDocuments(); // Refresh the documents list
      setDeleteDocDialogOpen(false);
      setDocumentToDelete(null);
      
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    } finally {
      setDeletingDocument(false);
    }
  };

  // Handle document menu open
  const handleDocMenuOpen = (event: React.MouseEvent<HTMLElement>, doc: UserDocumentDto) => {
    setDocMenuAnchorEl(event.currentTarget);
    setSelectedDocument(doc);
  };

  // Handle document menu close
  const handleDocMenuClose = () => {
    setDocMenuAnchorEl(null);
    setSelectedDocument(null);
  };

  // Handle delete confirmation dialog
  const handleDeleteClick = () => {
    if (selectedDocument) {
      setDocumentToDelete(selectedDocument);
      setDeleteDocDialogOpen(true);
      handleDocMenuClose();
    }
  };

  // Handle download from menu
  const handleDownloadFromMenu = async () => {
    if (selectedDocument && userGuid) {
      try {
        await UserDocumentApi.downloadDocument(userGuid, selectedDocument);
      } catch (err) {
        console.error('Error downloading document:', err);
        setError(err instanceof Error ? err.message : 'Failed to download document');
      }
      handleDocMenuClose();
    }
  };

  // Helper function to handle file click
  // View document in a new tab (same pattern as client Settings → Documents).
  const handleFileClick = async (doc: UserDocumentDto) => {
    if (!userGuid) return;
    try {
      await UserDocumentApi.viewDocument(userGuid, doc);
    } catch (err) {
      console.error('Error opening document:', err);
      setError(err instanceof Error ? err.message : 'Failed to open document');
    }
  };

  // Complete fetching user notes function that was interrupted
  useEffect(() => {
    if (userGuid) {
      fetchUser();
      fetchUserTickets();
      fetchUserNotes();
      fetchUserDocuments();
    }
  }, [userGuid]);

  // Load court events after tickets are loaded
  useEffect(() => {
    if (tickets.length > 0) {
      fetchCourtEvents();
    }
  }, [tickets]);

  // Save user changes
  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager-user/user/${user.User_guid}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            User_FirstName: formData.User_FirstName,
            User_MiddleName: formData.User_MiddleName,
            User_LastName: formData.User_LastName,
            // Blank clears the address rather than saving '' — the client simply has none on file.
            User_Email: formData.User_Email.trim() || null,
            User_DOB: formData.User_DOB,
            User_Active: formData.User_Active,
            User_PhoneNumber: formData.User_PhoneNumber || null,
            User_DriversLicenseNumber: formData.User_DriversLicenseNumber.trim() || null,
            User_LicenseType: formData.User_LicenseType || null,
            User_Source: formData.User_Source || null,
          }),
        }
      );

      if (!response.ok) {
        // Say what the server said. "409 Conflict" gives staff nothing to act on, where the message
        // behind it names the client already holding the address they just typed.
        const errorData = await response
          .json()
          .catch(() => ({} as { message?: string; errors?: Array<{ msg?: string }> }));
        const fieldErrors = Array.isArray(errorData.errors)
          ? errorData.errors.map((e: { msg?: string }) => e.msg).filter(Boolean)
          : [];
        throw new Error(
          errorData.message ||
            fieldErrors.join('; ') ||
            `Failed to update user: ${response.status} ${response.statusText}`,
        );
      }

      const updatedUser: UserDto = await response.json();
      setUser(updatedUser);
      setFormData({
        User_FirstName: updatedUser.User_FirstName,
        User_MiddleName: updatedUser.User_MiddleName || '',
        User_LastName: updatedUser.User_LastName,
        User_Email: updatedUser.User_Email || '',
        User_Active: updatedUser.User_Active,
        User_DOB: updatedUser.User_DOB,
        User_PhoneNumber: updatedUser.User_PhoneNumber || '',
        User_DriversLicenseNumber: updatedUser.User_DriversLicenseNumber || '',
        User_LicenseType: updatedUser.User_LicenseType || '',
        User_Source: updatedUser.User_Source || '',
      });
      setIsEditing(false);
      setSuccess('User updated successfully!');
      
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  // Soft-delete this client and their cases, then return to the Clients list.
  const handleDeleteClient = async () => {
    if (!user) return;
    setDeletingClient(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager-user/users/${user.User_guid}`,
        { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, credentials: 'include' },
      );
      if (!response.ok) {
        throw new Error(`Failed to delete client: ${response.status} ${response.statusText}`);
      }
      navigate('/users');
    } catch (err) {
      console.error('Error deleting client:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete client');
      setDeletingClient(false);
      setDeleteClientOpen(false);
    }
  };

  // Reset user password
  const handleResetPassword = async () => {
    if (!user || !newPassword.trim()) return;
    
    setResetPasswordLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await ManagerApi.resetUserPassword(user.User_guid, newPassword);
      setSuccess('Password reset successfully! User has been notified via email.');
      setResetPasswordOpen(false);
      setNewPassword('');
      
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleFormChange = (field: keyof UserFormData, value: string | boolean) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        User_FirstName: user.User_FirstName,
        User_MiddleName: user.User_MiddleName || '',
        User_LastName: user.User_LastName,
        User_Email: user.User_Email || '',
        User_Active: user.User_Active,
        User_DOB: user.User_DOB,
        User_PhoneNumber: user.User_PhoneNumber || '',
        User_DriversLicenseNumber: user.User_DriversLicenseNumber || '',
        User_LicenseType: user.User_LicenseType || '',
        User_Source: user.User_Source || '',
      });
    }
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleResetPasswordClose = () => {
    setResetPasswordOpen(false);
    setNewPassword('');
    setError(null);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Post a new status for a case (append-only history) and refresh the case list.
  const postTicketStatus = async (
    ticketGuid: string,
    statusName: string,
    statusDesc: string,
  ): Promise<boolean> => {
    setStatusUpdatingGuid(ticketGuid);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager/ticket-status/${ticketGuid}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            TicketStatus_Name: statusName,
            TicketStatus_Desc: statusDesc,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to update status: ${response.status} ${response.statusText}`);
      }

      setSuccess('Case status updated.');
      await fetchUserTickets(0, false);
      return true;
    } catch (err) {
      console.error('Error updating case status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update case status');
      return false;
    } finally {
      setStatusUpdatingGuid(null);
    }
  };

  const handleStageChange = (
    ticketGuid: string,
    statusName: string,
    stageLabel: string,
    matterLabel: string,
  ) => {
    setPendingStageChange({ ticketGuid, statusName, stageLabel, matterLabel });
  };

  const handleConfirmStageChange = async () => {
    if (!pendingStageChange) return;
    const { ticketGuid, statusName, stageLabel } = pendingStageChange;
    const ok = await postTicketStatus(
      ticketGuid,
      statusName,
      `Moved to "${stageLabel}" from the client profile`,
    );
    if (ok) {
      setPendingStageChange(null);
    }
  };

  const handleCancelStageChange = () => {
    if (statusUpdatingGuid) return;
    setPendingStageChange(null);
  };

  // Reopen a closed case back to an active (open) status.
  const handleReopenCase = (ticketGuid: string) => {
    postTicketStatus(
      ticketGuid,
      'case accepted (Approved)',
      'Case reopened from the client profile',
    );
  };

  // Court Events handlers
  /**
   * Add a court event from the client profile. A client can hold several cases, so the event has to
   * be told which one it belongs to — that is what the ticket picker in the dialog is for. Until
   * Jul 2026 this button only raised a browser alert saying the feature needed building; Lloyd hit
   * it trying to enter a pre-trial notice and could not add anything.
   */
  const handleCreateCourtEvent = () => {
    setEditingEvent(null);
    setError(null);
    // One case on file is not a choice — preselect it rather than making them pick from a list of one.
    setEventTicketGuid(tickets.length === 1 ? tickets[0].ticket.Ticket_guid : '');
    setEventForm({
      CourtEvent_StartDate: '',
      CourtEvent_StartTime: '',
      CourtEvent_EndDate: '',
      CourtEvent_EndTime: '',
      CourtEvent_Title: '',
      CourtEvent_Desc: '',
    });
    setShowEventDialog(true);
  };

  const handleEditCourtEvent = (event: CourtEventWithTicket) => {
    setEditingEvent(event);
    // Convert epoch time back to date/time strings for form inputs
    const startDate = new Date(parseInt('' + event.CourtEvent_Start));
    const endDate = new Date(parseInt('' + event.CourtEvent_End));
    
    setEventForm({
      CourtEvent_StartDate: startDate.toISOString().split('T')[0], // YYYY-MM-DD
      CourtEvent_StartTime: startDate.toTimeString().split(' ')[0].slice(0, 5), // HH:MM
      CourtEvent_EndDate: endDate.toISOString().split('T')[0], // YYYY-MM-DD
      CourtEvent_EndTime: endDate.toTimeString().split(' ')[0].slice(0, 5), // HH:MM
      CourtEvent_Title: event.CourtEvent_Title,
      CourtEvent_Desc: event.CourtEvent_Desc,
    });
    setShowEventDialog(true);
  };

  const handleDeleteCourtEvent = async (event: CourtEventWithTicket) => {
    if (!confirm('Are you sure you want to delete this court event?')) return;
    
    try {
      await CourtEventApi.deleteCourtEvent(event.CourtEvent_guid);
      setSuccess('Court event deleted successfully');
      fetchCourtEvents(); // Refresh the events list
    } catch (err) {
      console.error('Error deleting court event:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete court event');
    }
  };

  // Event Form validation functions
  /**
   * The first thing wrong with the form, phrased for the person looking at it, or null when it is
   * good to save. A single "check all fields" message is what made the old dialog impossible to
   * clear — it never said which field was the problem.
   *
   * Description is deliberately NOT required. A pre-trial notice often has nothing to add beyond
   * the date, the time and the title, and demanding prose invents busywork.
   */
  const courtEventFormProblem = (): string | null => {
    if (!editingEvent && !eventTicketGuid) return 'Choose which case this event belongs to.';
    if (!eventForm.CourtEvent_StartDate.trim()) return 'Enter a start date.';
    if (!eventForm.CourtEvent_StartTime.trim()) return 'Enter a start time.';
    if (!eventForm.CourtEvent_EndDate.trim()) return 'Enter an end date.';
    if (!eventForm.CourtEvent_EndTime.trim()) return 'Enter an end time.';
    if (!eventForm.CourtEvent_Title.trim()) return 'Enter a title for the event.';

    const startDateTime = new Date(`${eventForm.CourtEvent_StartDate}T${eventForm.CourtEvent_StartTime}`);
    const endDateTime = new Date(`${eventForm.CourtEvent_EndDate}T${eventForm.CourtEvent_EndTime}`);
    if (!(endDateTime > startDateTime)) return 'The end time must be after the start time.';

    return null;
  };

  const isCourtEventFormValid = () => courtEventFormProblem() === null;

  const handleSaveEvent = async () => {
    const problem = courtEventFormProblem();
    if (problem) {
      setError(problem);
      return;
    }

    setActionLoading(true);
    try {
      // Convert form date and time to epoch timestamps
      const startDateTimeString = `${eventForm.CourtEvent_StartDate}T${eventForm.CourtEvent_StartTime}`;
      const endDateTimeString = `${eventForm.CourtEvent_EndDate}T${eventForm.CourtEvent_EndTime}`;
      const startEpoch = new Date(startDateTimeString).getTime();
      const endEpoch = new Date(endDateTimeString).getTime();

      if (editingEvent) {
        await CourtEventApi.updateCourtEvent({
          CourtEvent_guid: editingEvent.CourtEvent_guid,
          CourtEvent_Start: startEpoch,
          CourtEvent_End: endEpoch,
          CourtEvent_Title: eventForm.CourtEvent_Title,
          CourtEvent_Desc: eventForm.CourtEvent_Desc,
        });
        setSuccess('Court event updated successfully');
      } else {
        await CourtEventApi.createCourtEvent({
          Ticket_guid: eventTicketGuid,
          CourtEvent_Start: startEpoch,
          CourtEvent_End: endEpoch,
          CourtEvent_Title: eventForm.CourtEvent_Title,
          CourtEvent_Desc: eventForm.CourtEvent_Desc,
        });
        setSuccess('Court event added');
      }

      setShowEventDialog(false);
      setEditingEvent(null);
      setEventTicketGuid('');
      fetchCourtEvents(); // Reload events
    } catch (err) {
      console.error('Error saving court event:', err);
      setError(err instanceof Error ? err.message : 'Failed to save court event');
    } finally {
      setActionLoading(false);
    }
  };

  // User Notes handlers
  const handleCreateNote = () => {
    setEditingNote(null);
    setNoteFormData({ title: '', description: '' });
    // Preselect when there is only one case to choose: with a single case the question has one
    // answer, and asking it anyway is how notes end up unfiled.
    setNoteTicketGuid(tickets.length === 1 ? tickets[0].ticket.Ticket_guid : '');
    setNoteDialogOpen(true);
  };

  const handleEditNote = (note: UserNoteDto) => {
    setEditingNote(note);
    setNoteFormData({ title: note.UserNote_Title, description: note.UserNote_Desc });
    setNoteTicketGuid(note.Ticket_guid ?? '');
    setNoteDialogOpen(true);
  };

  const handleDeleteNote = async (noteGuid: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/user-note?UserNote_guid=${noteGuid}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete note: ${response.status} ${response.statusText}`);
      }

      setSuccess('Note deleted successfully!');
      fetchUserNotes(); // Refresh the notes list
    } catch (err) {
      console.error('Error deleting note:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete note');
    }
  };

  const handleSaveNote = async () => {
    if (!userGuid || !noteFormData.title.trim() || !noteFormData.description.trim()) {
      setError('Please fill in both title and description');
      return;
    }

    setNoteSubmitting(true);
    setError(null);

    try {
      if (editingNote) {
        // Update existing note
        const response = await fetch(
          `${process.env.API_HOST || ''}/api/v1/user-note`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              UserNote_guid: editingNote.UserNote_guid,
              // Explicit null unfiles the note, which is a different instruction from omitting the
              // field — the route distinguishes the two, so editing must be able to say either.
              Ticket_guid: noteTicketGuid || null,
              UserNote_Title: noteFormData.title,
              UserNote_Desc: noteFormData.description,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update note: ${response.status} ${response.statusText}`);
        }

        setSuccess('Note updated successfully!');
      } else {
        // Create new note
        const response = await fetch(
          `${process.env.API_HOST || ''}/api/v1/user-note`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              User_guid: userGuid,
              Ticket_guid: noteTicketGuid || undefined,
              UserNote_Title: noteFormData.title,
              UserNote_Desc: noteFormData.description,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to create note: ${response.status} ${response.statusText}`);
        }

        setSuccess('Note created successfully!');
      }

      setNoteDialogOpen(false);
      fetchUserNotes(); // Refresh the notes list
    } catch (err) {
      console.error('Error saving note:', err);
      setError(err instanceof Error ? err.message : 'Failed to save note');
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleNoteDialogClose = () => {
    setNoteDialogOpen(false);
    setEditingNote(null);
    setNoteTicketGuid('');
    setNoteFormData({ title: '', description: '' });
    setError(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Generate a random password
  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$!%^&*';
    let password = '';
    
    // Ensure at least one of each required character type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // digit
    password += '@#$!%^&*'[Math.floor(Math.random() * 8)]; // special char
    
    // Fill the rest randomly
    for (let i = 4; i < 12; i++) {
      password += chars[Math.floor(Math.random() * chars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  // Open/closed view: filter the nested cases by whether they are in the closed stage.
  const visibleTickets = tickets.filter((ct) => {
    if (caseFilter === 'all') return true;
    const closed =
      stageForStatus(ct.ticketStatus?.TicketStatus_Name) === CLOSED_STAGE_INDEX;
    return caseFilter === 'closed' ? closed : !closed;
  });

  if (!userGuid) {
    return (
      <>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">No user ID provided in URL</Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            User Details
          </Typography>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : user ? (
          <>
            {/* User Information Card */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">User Information</Typography>
                {!isEditing && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<LockResetIcon />}
                      onClick={() => setResetPasswordOpen(true)}
                      variant="outlined"
                      size="small"
                      color="warning"
                    >
                      Reset Password
                    </Button>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      variant="outlined"
                      size="small"
                    >
                      Edit User
                    </Button>
                    <Button
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteClientOpen(true)}
                      variant="outlined"
                      size="small"
                      color="error"
                    >
                      Delete Client
                    </Button>
                  </Box>
                )}
              </Box>

              {!isEditing ? (
                // Compact read-only info with active links (Lloyd, Jul 8: the greyed-out disabled
                // form filled the screen and the email/phone weren't clickable).
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, rowGap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Name</Typography>
                    <Typography variant="body1">{clientDisplayName(user)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                    {user.User_Email ? (
                      <MuiLink href={`mailto:${user.User_Email}`} underline="hover">{user.User_Email}</MuiLink>
                    ) : '—'}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Phone</Typography>
                    {user.User_PhoneNumber ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <MuiLink href={`tel:${user.User_PhoneNumber}`} underline="hover">{user.User_PhoneNumber}</MuiLink>
                        <MuiLink href={`sms:${user.User_PhoneNumber}`} underline="hover">Text</MuiLink>
                      </Box>
                    ) : '—'}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Date of Birth</Typography>
                    <Typography variant="body1">{user.User_DOB ? formatDateOnly(user.User_DOB) : '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                    <Chip
                      label={user.User_Active ? 'Active' : 'Inactive'}
                      color={user.User_Active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Email verified</Typography>
                    <Chip
                      label={user.User_EmailVerified ? 'Verified' : 'Not verified'}
                      color={user.User_EmailVerified ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">User Driver&apos;s License #</Typography>
                    <Typography variant="body1">{user.User_DriversLicenseNumber || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">User License Type</Typography>
                    <Typography variant="body1">{user.User_LicenseType || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Source</Typography>
                    <Typography variant="body1">{user.User_Source || '—'}</Typography>
                  </Box>
                </Box>
              ) : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="First Name"
                    value={formData.User_FirstName}
                    onChange={(e) => handleFormChange('User_FirstName', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    variant={isEditing ? 'outlined' : 'filled'}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Middle Name"
                    value={formData.User_MiddleName}
                    onChange={(e) => handleFormChange('User_MiddleName', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    variant={isEditing ? 'outlined' : 'filled'}
                    helperText="Optional"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Last Name"
                    value={formData.User_LastName}
                    onChange={(e) => handleFormChange('User_LastName', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    variant={isEditing ? 'outlined' : 'filled'}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Email"
                    value={formData.User_Email}
                    onChange={(e) => handleFormChange('User_Email', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    variant={isEditing ? 'outlined' : 'filled'}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Phone Number"
                    value={formData.User_PhoneNumber}
                    onChange={(e) => handleFormChange('User_PhoneNumber', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    variant={isEditing ? 'outlined' : 'filled'}
                    type="tel"
                    placeholder="(555) 123-4567"
                    helperText="Phone number (optional)"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Date of Birth"
                    value={formData.User_DOB}
                    onChange={(e) => handleFormChange('User_DOB', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    type="date"
                    variant={isEditing ? 'outlined' : 'filled'}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.User_Active}
                        onChange={(e) => handleFormChange('User_Active', e.target.checked)}
                        disabled={!isEditing}
                      />
                    }
                    label="Active User"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="User Driver's License #"
                    value={formData.User_DriversLicenseNumber}
                    onChange={(e) => handleFormChange('User_DriversLicenseNumber', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    variant={isEditing ? 'outlined' : 'filled'}
                    helperText="One per client (optional)"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth disabled={!isEditing} variant={isEditing ? 'outlined' : 'filled'}>
                    <InputLabel id="user-license-type-label">User License Type</InputLabel>
                    <Select
                      labelId="user-license-type-label"
                      label="User License Type"
                      value={formData.User_LicenseType}
                      onChange={(e) => handleFormChange('User_LicenseType', e.target.value)}
                    >
                      <MenuItem value=""><em>Not specified</em></MenuItem>
                      <MenuItem value="CDL">CDL</MenuItem>
                      <MenuItem value="Regular">Regular</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth disabled={!isEditing} variant={isEditing ? 'outlined' : 'filled'}>
                    <InputLabel id="user-source-label">Source</InputLabel>
                    <Select
                      labelId="user-source-label"
                      label="Source"
                      value={formData.User_Source}
                      onChange={(e) => handleFormChange('User_Source', e.target.value)}
                    >
                      <MenuItem value=""><em>Not specified</em></MenuItem>
                      {SOURCE_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={user.User_EmailVerified ? 'Email Verified' : 'Email Not Verified'}
                      color={user.User_EmailVerified ? 'success' : 'warning'}
                      size="small"
                    />
                    <Chip
                      label={`TOS: ${formatDate(user.User_TermsOfService)}`}
                      color="info"
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>
              )}

              {/* Which engagement agreement this client is on (Lloyd, Aug 21 2026). Self-fetching,
                  so it stays correct whether or not the details form is in edit mode. */}
              {!isEditing && userGuid && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <AgreementVersionPanel User_guid={userGuid} />
                </Box>
              )}

              {/* Action Buttons */}
              {isEditing && (
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Tabbed Content */}
            <Paper sx={{ p: 0 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}
              >
                <Tab label={`User Tickets (${tickets.length})`} />
                <Tab label={`User Notes (${userNotes.length})`} />
                <Tab label={`Files (${userDocuments.length})`} />
                <Tab label="Messages" />
                <Tab label={`Court Events (${courtEvents.length})`} />
              </Tabs>

              {/* Tab Content */}
              <Box sx={{ p: 3 }}>
                {/* User Tickets Tab */}
                {activeTab === 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <TicketIcon sx={{ mr: 1 }} />
                        Cases ({tickets.length})
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => navigate(`/new-ticket?User_guid=${userGuid}`)}
                      >
                        Add Case
                      </Button>
                    </Box>

                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={caseFilter}
                      onChange={(_e, val) => { if (val) setCaseFilter(val); }}
                      sx={{ mb: 2 }}
                    >
                      <ToggleButton value="all">All</ToggleButton>
                      <ToggleButton value="open">Open</ToggleButton>
                      <ToggleButton value="closed">Closed</ToggleButton>
                    </ToggleButtonGroup>

                    {ticketsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : tickets.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', p: 2 }}>
                        No cases found for this client.
                      </Typography>
                    ) : visibleTickets.length === 0 ? (
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          No {caseFilter} cases in the loaded results.
                          {ticketsHasMore ? ' Load more to search additional cases.' : ''}
                        </Typography>
                        {ticketsHasMore && (
                          <Box sx={{ pt: 2 }}>
                            <Button
                              variant="outlined"
                              onClick={handleLoadMoreTickets}
                              disabled={ticketsLoadingMore}
                              startIcon={ticketsLoadingMore ? <CircularProgress size={20} /> : undefined}
                            >
                              {ticketsLoadingMore
                                ? 'Loading...'
                                : `Load More (Showing ${tickets.length} results)`}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <TableContainer>
                        <Box sx={{ pb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Showing {visibleTickets.length === tickets.length
                              ? `${tickets.length}`
                              : `${visibleTickets.length} matching (${tickets.length} loaded)`}
                            {' '}case{tickets.length === 1 ? '' : 's'}
                            {ticketsHasMore ? ' (more available)' : ''}
                          </Typography>
                        </Box>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Matter #</TableCell>
                              <TableCell>Ticket #</TableCell>
                              <TableCell>Offense</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Court Date</TableCell>
                              <TableCell>Court</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {visibleTickets.map((contextTicket) => {
                              const court = contextTicket.court;
                              const caseStatusName = contextTicket.ticketStatus?.TicketStatus_Name;
                              const isClosed = stageForStatus(caseStatusName) === CLOSED_STAGE_INDEX;
                              const isStatusUpdating = statusUpdatingGuid === contextTicket.ticket.Ticket_guid;
                              return (
                              <React.Fragment key={contextTicket.ticket.Ticket_guid}>
                              <TableRow
                                hover
                                sx={{ cursor: 'pointer', '& td': { borderBottom: 'none' }, opacity: isClosed ? 0.55 : 1 }}
                                onClick={() => navigate(`/ticket?Ticket_guid=${contextTicket.ticket.Ticket_guid}`)}
                              >
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="primary"
                                    sx={{ fontWeight: 'medium' }}
                                  >
                                    {contextTicket.ticket.Ticket_MatterNumber || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {contextTicket.ticket.Ticket_Number?.trim()
                                    ? contextTicket.ticket.Ticket_Number
                                    : '—'}
                                </TableCell>
                                <TableCell>{contextTicket.ticket.Ticket_Description || contextTicket.ticket.Ticket_Citation || '—'}</TableCell>
                                <TableCell>
                                  {contextTicket.ticketStatus && (
                                    <Chip
                                      label={contextTicket.ticketStatus.TicketStatus_Name}
                                      color={getStatusColor(contextTicket.ticketStatus.TicketStatus_Name)}
                                      size="small"
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {contextTicket.ticket.Ticket_CourtDate
                                    ? new Date(contextTicket.ticket.Ticket_CourtDate).toLocaleDateString()
                                    : '—'}
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  {court ? (
                                    <Box>
                                      <Typography variant="body2">{court.Court_Name}</Typography>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {court.Court_Email && (
                                          <MuiLink href={`mailto:${court.Court_Email}`} underline="hover" variant="caption">
                                            Email
                                          </MuiLink>
                                        )}
                                        {court.Court_Phone && (
                                          <MuiLink href={`tel:${court.Court_Phone}`} underline="hover" variant="caption">
                                            Call
                                          </MuiLink>
                                        )}
                                      </Box>
                                    </Box>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell colSpan={6} sx={{ pt: 0, pb: 1.5 }} onClick={(e) => e.stopPropagation()}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <CaseStatusTimeline
                                        statusName={caseStatusName}
                                        matterType={contextTicket.ticket.Ticket_MatterType}
                                        currentStageIndex={deriveStageIndex(
                                          contextTicket.ticket,
                                          caseStatusName,
                                        )}
                                        onSelectStage={(newStatus, stageLabel) =>
                                          handleStageChange(
                                            contextTicket.ticket.Ticket_guid,
                                            newStatus,
                                            stageLabel,
                                            contextTicket.ticket.Ticket_MatterNumber ||
                                              contextTicket.ticket.Ticket_Number ||
                                              'this case',
                                          )
                                        }
                                        disabled={isStatusUpdating}
                                        showOfferDetail
                                        ticketGuid={contextTicket.ticket.Ticket_guid}
                                        facts={{
                                          discoveryRequested: Boolean(
                                            contextTicket.ticket
                                              .Ticket_DiscoveryRequestedAt,
                                          ),
                                          discoveryReceived: Boolean(
                                            contextTicket.ticket
                                              .Ticket_DiscoveryReceivedAt,
                                          ),
                                        }}
                                      />
                                    </Box>
                                    {isClosed && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<LockOpenIcon />}
                                        disabled={isStatusUpdating}
                                        onClick={() => handleReopenCase(contextTicket.ticket.Ticket_guid)}
                                      >
                                        Reopen
                                      </Button>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                              </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>

                        {ticketsHasMore && (
                          <Box sx={{ pt: 3, textAlign: 'center' }}>
                            <Button
                              variant="outlined"
                              onClick={handleLoadMoreTickets}
                              disabled={ticketsLoadingMore}
                              startIcon={ticketsLoadingMore ? <CircularProgress size={20} /> : undefined}
                            >
                              {ticketsLoadingMore
                                ? 'Loading...'
                                : `Load More (Showing ${tickets.length} results)`}
                            </Button>
                          </Box>
                        )}
                      </TableContainer>
                    )}
                  </Box>
                )}

                {/* User Notes Tab */}
                {activeTab === 1 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <NoteIcon sx={{ mr: 1 }} />
                        User Notes ({userNotes.length})
                      </Typography>
                      <Button 
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNote}
                      >
                        Add Note
                      </Button>
                    </Box>

                    {notesLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : userNotes.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', p: 2 }}>
                        No notes found for this user.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {userNotes.map((note) => (
                          <Card key={note.UserNote_guid} variant="outlined">
                            <CardContent>
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'flex-end',
                                  m: 0 
                                }}
                              >
                                <Box>
                                  <Typography sx={{lineHeight: 1,}} variant="h6" component="h3">
                                    {note.UserNote_Title}
                                  </Typography>
                                  {/*
                                    Which case this note is about. Previously absent entirely, so a
                                    client with four cases showed four cases' notes in one
                                    undifferentiated list — the confusion Hannah predicted.

                                    An unfiled note says so rather than showing nothing: "no chip"
                                    and "chip we forgot to render" look identical, and only one of
                                    them is a fact about the note.
                                  */}
                                  <Chip
                                    size="small"
                                    sx={{ mt: 0.75 }}
                                    variant={note.Ticket_guid ? 'filled' : 'outlined'}
                                    label={
                                      note.Ticket_guid
                                        ? (() => {
                                            const ct = tickets.find(
                                              (t) => t.ticket.Ticket_guid === note.Ticket_guid,
                                            );
                                            if (!ct) return 'On another case';
                                            return (
                                              ct.ticket.Ticket_Number || 'Case with no citation number'
                                            );
                                          })()
                                        : 'About the client, not a case'
                                    }
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleEditNote(note)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleDeleteNote(note.UserNote_guid)}
                                  >
                                    Delete
                                  </Button>
                                </Box>
                              </Box>
                              {note.Ticket_guid && (
                                <Box sx={{margin: 0, lineHeight: 0.8}}>
                                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }} to={`/ticket?Ticket_guid=${note.Ticket_guid}`} component={Link}>
                                    {(() => {
                                      const associatedTicket = tickets.find(t => t.ticket.Ticket_guid === note.Ticket_guid);
                                      return associatedTicket ? `Ticket: ${associatedTicket.ticket.Ticket_Number}` : 'View associated ticket';
                                    })()}
                                  </Typography>
                                  
                                </Box>
                              )}
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                                {note.UserNote_Desc}
                              </Typography>
                              {(note.UserNote_AuthorName || note.UserNote_CreatedAt) && (
                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                                  {note.UserNote_AuthorName || 'Staff'}
                                  {note.UserNote_CreatedAt
                                    ? ` · ${new Date(note.UserNote_CreatedAt).toLocaleString()}`
                                    : ''}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Files Tab */}
                {activeTab === 2 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachFileIcon sx={{ mr: 1 }} />
                        User Files ({userDocuments.length})
                      </Typography>
                      <Button 
                        variant="contained"
                        startIcon={<UploadFileIcon />}
                        component="label"
                        disabled={uploadingDocument}
                      >
                        {uploadingDocument ? 'Uploading...' : 'Upload Files'}
                        <input
                          type="file"
                          hidden
                          multiple
                          accept={ACCEPTED_DOCUMENT_TYPES}
                          onChange={(e) => {
                            const files = Array.from(e.target.files ?? []);
                            e.target.value = ''; // Reset input
                            if (files.length > 0) {
                              handleDocumentUpload(files);
                            }
                          }}
                        />
                      </Button>
                    </Box>

                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Select several files at once (up to {MAX_BATCH_FILES}). Accepted types: PDF,
                      images, Word (max 50&nbsp;MB each).
                    </Typography>

                    {documentUploadFailures.length > 0 && (
                      <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        onClose={() => setDocumentUploadFailures([])}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {documentUploadFailures.length} file
                          {documentUploadFailures.length === 1 ? '' : 's'} could not be uploaded:
                        </Typography>
                        {documentUploadFailures.map((f) => (
                          <Typography key={f.name} variant="caption" sx={{ display: 'block' }}>
                            {f.name} — {f.error}
                          </Typography>
                        ))}
                      </Alert>
                    )}

                    {documentsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : userDocuments.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', p: 2 }}>
                        No files found for this user.
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>File Name</TableCell>
                              <TableCell>Upload Date</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {userDocuments.map((doc) => (
                              <TableRow key={doc.UserDocument_id} hover>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <AttachFileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2">
                                      {doc.UserDocument_Name}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {new Date(doc.UserDocument_CreatedAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleFileClick(doc)}
                                    >
                                      View
                                    </Button>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleDocMenuOpen(e, doc)}
                                      title="More actions"
                                    >
                                      <MoreVertIcon />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}

                {/* Messages Tab */}
                {activeTab === 3 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', p: 4 }}>
                      <MessageIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        User Messages
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
                        View and manage all messages for this user
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<MessageIcon />}
                        onClick={() => navigate(`/message?User_guid=${userGuid}`)}
                        size="large"
                      >
                        View Messages
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Court Events Tab */}
                {activeTab === 4 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <EventIcon sx={{ mr: 1 }} />
                        Court Events ({courtEvents.length})
                      </Typography>
                    </Box>

                    <ManagerTicketsList
                      courtEvents={courtEvents}
                      courtEventsLoading={courtEventsLoading}
                      courtEventError={courtEventError}
                      actionLoading={false}
                      onCreateEvent={handleCreateCourtEvent}
                      onEditEvent={handleEditCourtEvent}
                      onDeleteEvent={handleDeleteCourtEvent}
                      disableCreateButton={true}
                      createButtonTooltip="Create court events on the tickets page"
                    />
                  </Box>
                )}
              </Box>
            </Paper>

            
          </>
        ) : (
          <Alert severity="error">User not found</Alert>
        )}

        {/* Note Dialog */}
        <Dialog 
          open={noteDialogOpen} 
          onClose={handleNoteDialogClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingNote ? 'Edit Note' : 'Create New Note'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Note Title"
              fullWidth
              variant="outlined"
              value={noteFormData.title}
              onChange={(e) => setNoteFormData({ ...noteFormData, title: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Note Description"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={noteFormData.description}
              onChange={(e) => setNoteFormData({ ...noteFormData, description: e.target.value })}
            />
            {/*
              Which case this note is about (Hannah, Aug 18; Lloyd agreed). Without it every note
              written on this screen was unfiled forever, and on a client with four cases nobody
              could tell which one it referred to.

              "About the client, not a case" stays available on purpose — a note about somebody's
              circumstances is not a note about a citation, and forcing a case would put notes on
              arbitrary ones.
            */}
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel id="note-case-label">Which case is this note about?</InputLabel>
              <Select
                labelId="note-case-label"
                label="Which case is this note about?"
                value={noteTicketGuid}
                onChange={(e) => setNoteTicketGuid(e.target.value)}
              >
                <MenuItem value="">
                  <em>About the client, not a specific case</em>
                </MenuItem>
                {tickets.map((ct) => (
                  <MenuItem key={ct.ticket.Ticket_guid} value={ct.ticket.Ticket_guid}>
                    {[
                      ct.ticket.Ticket_Number || 'No citation number',
                      ct.ticket.Ticket_Description,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleNoteDialogClose}
              disabled={noteSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveNote}
              variant="contained"
              disabled={noteSubmitting || !noteFormData.title.trim() || !noteFormData.description.trim()}
            >
              {noteSubmitting ? 'Saving...' : (editingNote ? 'Update Note' : 'Create Note')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Password Reset Dialog */}
        <Dialog 
          open={resetPasswordOpen} 
          onClose={handleResetPasswordClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reset User Password</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Reset password for {user?.User_FirstName} {user?.User_LastName}
              {user?.User_Email ? ` (${user.User_Email})` : ''}
              {!user?.User_Email && ' — no email on file, so they will not be notified'}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="New Password"
              type="text"
              fullWidth
              variant="outlined"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Password must be 8-20 characters with uppercase, lowercase, number, and special character"
              sx={{ mb: 2 }}
            />
            {/* <Button
              onClick={() => setNewPassword(generateRandomPassword())}
              variant="outlined"
              size="small"
              fullWidth
            >
              Generate Random Password
            </Button> */}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleResetPasswordClose}
              disabled={resetPasswordLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword}
              variant="contained"
              color="warning"
              disabled={resetPasswordLoading || !newPassword.trim()}
            >
              {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Client Confirmation */}
        <Dialog
          open={deleteClientOpen}
          onClose={() => { if (!deletingClient) setDeleteClientOpen(false); }}
        >
          <DialogTitle>Delete Client</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete {user?.User_FirstName} {user?.User_LastName} and all of their cases? They will
              be removed from all lists. Payment history and notes are preserved for the record, and
              the client can be restored later if needed.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteClientOpen(false)} disabled={deletingClient}>
              Cancel
            </Button>
            <Button onClick={handleDeleteClient} color="error" variant="contained" disabled={deletingClient}>
              {deletingClient ? 'Deleting...' : 'Delete Client'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document Actions Menu */}
        <Menu
          anchorEl={docMenuAnchorEl}
          open={Boolean(docMenuAnchorEl)}
          onClose={handleDocMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleDownloadFromMenu}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Delete Document Confirmation Dialog */}
        <Dialog
          open={deleteDocDialogOpen}
          onClose={() => {
            if (!deletingDocument) {
              setDeleteDocDialogOpen(false);
              setDocumentToDelete(null);
            }
          }}
          aria-labelledby="delete-doc-dialog-title"
          aria-describedby="delete-doc-dialog-description"
        >
          <DialogTitle id="delete-doc-dialog-title">
            Delete Document
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-doc-dialog-description">
              Are you sure you want to delete "{documentToDelete?.UserDocument_Name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setDeleteDocDialogOpen(false);
                setDocumentToDelete(null);
              }} 
              disabled={deletingDocument}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => documentToDelete && handleDeleteDocument(documentToDelete)} 
              color="error" 
              variant="contained"
              disabled={deletingDocument}
            >
              {deletingDocument ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Event Edit Modal */}
        <Dialog 
          open={showEventDialog} 
          onClose={actionLoading ? undefined : () => setShowEventDialog(false)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            {editingEvent ? 'Edit Court Event' : 'Add Court Event'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              {/* Which case the event belongs to — only a new event needs asking. */}
              {!editingEvent && (
                <FormControl fullWidth disabled={actionLoading}>
                  <InputLabel id="event-ticket-label">Case</InputLabel>
                  <Select
                    labelId="event-ticket-label"
                    label="Case"
                    value={eventTicketGuid}
                    onChange={(e) => setEventTicketGuid(e.target.value)}
                  >
                    {tickets.map((t) => (
                      <MenuItem key={t.ticket.Ticket_guid} value={t.ticket.Ticket_guid}>
                        {t.ticket.Ticket_Number || 'Unnumbered case'}
                        {t.court?.Court_Name ? ` — ${t.court.Court_Name}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {!editingEvent && tickets.length === 0 && (
                <Alert severity="warning">
                  This client has no cases on file yet. A court event has to belong to a case — add
                  the citation first, then come back.
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={eventForm.CourtEvent_StartDate}
                  onChange={(e) => setEventForm(prev => ({ ...prev, CourtEvent_StartDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  disabled={actionLoading}
                />
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={eventForm.CourtEvent_StartTime}
                  onChange={(e) => setEventForm(prev => ({ ...prev, CourtEvent_StartTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  disabled={actionLoading}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={eventForm.CourtEvent_EndDate}
                  onChange={(e) => setEventForm(prev => ({ ...prev, CourtEvent_EndDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  disabled={actionLoading}
                />
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={eventForm.CourtEvent_EndTime}
                  onChange={(e) => setEventForm(prev => ({ ...prev, CourtEvent_EndTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  disabled={actionLoading}
                />
              </Box>
              <TextField
                fullWidth
                label="Event Title"
                value={eventForm.CourtEvent_Title}
                onChange={(e) => setEventForm(prev => ({ ...prev, CourtEvent_Title: e.target.value }))}
                disabled={actionLoading}
              />
              <TextField
                fullWidth
                label="Event Description (optional)"
                multiline
                rows={3}
                value={eventForm.CourtEvent_Desc}
                onChange={(e) => setEventForm(prev => ({ ...prev, CourtEvent_Desc: e.target.value }))}
                disabled={actionLoading}
              />

              {/* Name the one thing that is wrong, so a disabled Save button is never a mystery. */}
              {courtEventFormProblem() && (
                <Typography variant="caption" color="text.secondary">
                  {courtEventFormProblem()}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEventDialog(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSaveEvent} 
              disabled={actionLoading || !isCourtEventFormValid()}
            >
              {actionLoading ? 'Saving...' : 'Save Event'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Case stage change confirmation */}
        <Dialog
          open={pendingStageChange !== null}
          onClose={handleCancelStageChange}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Change case stage?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {pendingStageChange && (
                <>
                  Move matter <strong>{pendingStageChange.matterLabel}</strong> to{' '}
                  <strong>{pendingStageChange.stageLabel}</strong>?
                  <br />
                  <br />
                  This adds a new status entry to the case history.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelStageChange} disabled={Boolean(statusUpdatingGuid)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmStageChange}
              disabled={Boolean(statusUpdatingGuid)}
              startIcon={statusUpdatingGuid ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {statusUpdatingGuid ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ManagerUserView;
